import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export const inputCls =
  "w-full border border-gold/20 bg-white/[0.04] px-4 py-3.5 text-sm text-white outline-none transition-all placeholder:text-slate-500 focus:border-gold focus:ring-1 focus:ring-gold/40";

export const selectCls = `${inputCls} appearance-none bg-ink-2`;

export const parseNum = (s) => Number(String(s).replace(/[.\s]/g, "").replace(",", "."));

export const fmt = (n) => new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(n);

export const fmtPct = (n) => `${String(n).replace(".", ",")} %`;

export const Field = ({ label, hint, children }) => (
  <div>
    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">{label}</label>
    {children}
    {hint ? <p className="mt-2 text-xs leading-relaxed text-slate-500">{hint}</p> : null}
  </div>
);

export const ChoiceGroup = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2.5" data-testid={testid}>
    {options.map((o) => (
      <button
        key={o.v}
        type="button"
        data-testid={`${testid}-${o.v}`}
        onClick={() => onChange(o.v)}
        className={`border px-4 py-2.5 text-sm font-medium transition-all duration-300 ${
          value === o.v
            ? "border-gold bg-gold text-ink"
            : "border-gold/25 bg-white/[0.03] text-slate-300 hover:border-gold/60 hover:text-gold"
        }`}
      >
        {o.l}
      </button>
    ))}
  </div>
);

export const Slider = ({ label, value, min, max, onChange, testid, suffix = "" }) => (
  <div data-testid={testid}>
    <div className="mb-2 flex items-baseline justify-between">
      <label className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">{label}</label>
      <span className="font-serif text-lg text-white" data-testid={`${testid}-value`}>
        {value > 0 ? `+${value}` : value}{suffix}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full accent-gold"
      data-testid={`${testid}-slider`}
    />
  </div>
);

export const GoldButton = ({ children, loading, testid, type = "submit", onClick, full = true }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={loading}
    data-testid={testid}
    className={`flex items-center justify-center gap-3 bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-ink transition-all duration-300 hover:bg-gold-light disabled:opacity-60 ${full ? "w-full" : ""}`}
  >
    {children}
  </button>
);

export const ConsentChecks = ({ lead, setLead, prefix }) => (
  <>
    <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-slate-300">
      <input
        type="checkbox"
        checked={lead.consent_privacidad}
        onChange={(e) => setLead({ ...lead, consent_privacidad: e.target.checked })}
        className="mt-1 h-4 w-4 shrink-0 accent-gold"
        data-testid={`${prefix}-check-privacy`}
      />
      <span>
        He leído y acepto la{" "}
        <Link to="/privacidad" className="text-gold underline underline-offset-2" data-testid={`${prefix}-privacy-link`}>
          política de privacidad
        </Link>
        . *
      </span>
    </label>
    <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-slate-300">
      <input
        type="checkbox"
        checked={lead.consent_comercial}
        onChange={(e) => setLead({ ...lead, consent_comercial: e.target.checked })}
        className="mt-1 h-4 w-4 shrink-0 accent-gold"
        data-testid={`${prefix}-check-commercial`}
      />
      <span>Acepto recibir comunicaciones comerciales de AJG M&amp;A Consulting.</span>
    </label>
  </>
);

export const DonePanel = ({ title, subtitle, extra, testid }) => (
  <div className="border border-gold/25 bg-ink-3/60 p-10 text-center md:p-14" data-testid={testid}>
    <p className="font-serif text-3xl leading-snug text-white">{title}</p>
    <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-slate-300">{subtitle}</p>
    {extra}
  </div>
);

export const ToolShell = ({ eyebrow, title, intro, children }) => (
  <div className="min-h-screen">
    <header className="border-b border-gold/10">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-6 md:px-10">
        <Link to="/" data-testid="tool-logo">
          <img src="/logo-light.png" alt="AJG M&A Consulting" className="h-12 w-auto" />
        </Link>
        <Link
          to="/"
          data-testid="tool-back"
          className="group flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-300 transition-colors hover:text-gold"
        >
          <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
          Volver al inicio
        </Link>
      </div>
    </header>
    <main className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-24">
      <p className="eyebrow">{eyebrow}</p>
      <h1 className="mt-6 max-w-3xl font-serif text-4xl leading-tight text-white sm:text-5xl">{title}</h1>
      <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">{intro}</p>
      {children}
    </main>
    <footer className="border-t border-gold/10 py-8">
      <p className="mx-auto max-w-7xl px-6 text-xs tracking-wide text-slate-500 md:px-10">
        © 2026 AJG M&amp;A Consulting, S.L. Todos los derechos reservados.
      </p>
    </footer>
  </div>
);
